/* Used global Wordpress php variables to the ajax requests */
/* global themeVars */

/**
 * Data Brokers Navigation Class
 * Manages the navigation and filtering of data brokers.
 */
export class DataBrokersNavigation {


	/**
	 * Creates an instance of DataBrokersNavigation.
	 * @param {HTMLElement} block - The main container element for the data brokers navigation.
	 */
	constructor( block ) {

		if ( block === null ) {

			return;

		}

		// Initialize properties
		this.dataBrokersNavigationBlock = block;
		this.resultsContainer = this.dataBrokersNavigationBlock.querySelector( '.brokers-results' );
		this.filtersInputs = this.dataBrokersNavigationBlock.querySelectorAll( '.brokers-filters__form input' );
		this.searchInput = this.dataBrokersNavigationBlock.querySelector( '.brokers-search__input' );
		this.paginationControls = this.dataBrokersNavigationBlock.querySelector( '.brokers-pagination__controls' );
		this.filtersContainer = this.dataBrokersNavigationBlock.querySelector( '.brokers-filters' );
		this.closeFiltersButton = this.dataBrokersNavigationBlock.querySelector( '.js-brokers-filters-close' );
		this.openFiltersButton = this.dataBrokersNavigationBlock.querySelector( '.js-mobile-filters-button' );
		this.filtersOverlay = this.dataBrokersNavigationBlock.querySelector( '.js-brokers-filters-overlay' );
		this.viewSwitcherButtons = this.dataBrokersNavigationBlock.querySelectorAll( '.js-view-switcher button' );

		this.handleFilterChange = this.handleFilterChange.bind( this );

		// Initialize state from URL
		const searchParams = new URLSearchParams( window.location.search );
		this.currentPage = parseInt( searchParams.get( 'broker_page' ) ) || 1;
		this.currentViewType = searchParams.get( 'view' ) || 'list';
		this.currentFilters = {};

		// Store initial page value
		this.initialPage = this.currentPage;

		this.instanceVars = themeVars;

		// Determine default view based on screen width only if not set in URL
		if ( !searchParams.get( 'view' ) ) {

			const isMobile = window.innerWidth <= 1024;
			this.currentViewType = isMobile ? 'grid' : 'list';

		}

		// Update view switcher state
		this.updateViewSwitcherState();

		// Setup event listeners
		this.setupEventListeners();

		// Initialize filters inputs
		this.initFiltersInputs();

		// Initialize filters from URL and check corresponding inputs
		this.initializeFiltersFromUrl();

		// Handle search parameter if present
		const searchValue = searchParams.get( 'search' );
		if ( searchValue && this.searchInput ) {

			this.searchInput.value = searchValue;
			if ( !this.currentFilters.search ) {

				this.currentFilters.search = [];

			}
			this.currentFilters.search = [searchValue];

		}

		// Add resize handler
		this.handleResize = this.handleResize.bind( this );
		window.addEventListener( 'resize', this.debounce( this.handleResize, 250 ) );

		// Add new properties
		this.suggestionsContainer = this.dataBrokersNavigationBlock.querySelector( '.brokers-search__suggestions' );
		this.isSuggestionsVisible = false;

		// Bind methods
		this.handleSearchInput = this.handleSearchInput.bind( this );
		this.handleSuggestionClick = this.handleSuggestionClick.bind( this );
		this.handleClickOutside = this.handleClickOutside.bind( this );

		// Setup suggestions event listeners
		this.setupSuggestionsEventListeners();

	}

	/**
	 * Sets up all event listeners for the data brokers navigation.
	 */
	setupEventListeners() {

		this.viewSwitcherButtons.forEach( ( button ) => {

			button.addEventListener( 'click', ( event ) => {

				const viewButton = event.target.closest( 'button' );
				if ( viewButton ) {

					this.currentViewType = viewButton.dataset.type;
					this.updateViewSwitcherState();
					this.updateUrlParams(); // Add this line to update URL
					this.updateResults();

				}

			} );

		} );

		this.searchInput.addEventListener( 'input', this.debounce( () => {

			if ( window.innerWidth > 1024 ) {

				this.updateFilters( 'search', this.searchInput.value );

			}

		}, 300 ) );

		this.paginationControls.addEventListener( 'click', ( event ) => {

			const pageLink = event.target.closest( 'a[data-page]' );
			if ( pageLink && !pageLink.classList.contains( 'inactive' ) ) {

				event.preventDefault(); // Prevent the default link behavior
				const newPage = parseInt( pageLink.dataset.page, 10 );
				if ( newPage !== this.currentPage ) {

					this.currentPage = newPage;
					this.updateUrlParams();
					this.updateResults();

				}

			}

		} );

		if ( this.closeFiltersButton ) {

			this.closeFiltersButton.addEventListener( 'click', this.closeFilters.bind( this ) );

		}

		if ( this.openFiltersButton ) {

			this.openFiltersButton.addEventListener( 'click', this.openFilters.bind( this ) );

		}

		if ( this.filtersOverlay ) {

			this.filtersOverlay.addEventListener( 'click', this.closeFilters.bind( this ) );

		}

		// Add show results button click handler
		const showResultsButton = this.dataBrokersNavigationBlock.querySelector( '.js-show-results' );
		if ( showResultsButton ) {

			showResultsButton.addEventListener( 'click', () => {

				this.closeFilters();

			} );

		}

		// Add clear filters button click handler
		const clearFiltersButton = this.dataBrokersNavigationBlock.querySelector( '.js-clear-filters' );
		if ( clearFiltersButton ) {

			clearFiltersButton.addEventListener( 'click', this.clearAllFilters.bind( this ) );

		}

	}

	/**
	 * Updates the results based on current filters, page, and view type.
	 */
	async updateResults() {

		try {

			/* eslint-disable camelcase */
			// Create the request body
			const requestBody = {
				action: 'fetch_data_brokers',
				nonce: this.instanceVars.security,
				broker_page: this.currentPage,
				viewType: this.currentViewType,
				filters: JSON.stringify( this.currentFilters ),
				search: this.searchInput.value // Add separate search parameter
			};
			/* eslint-enable camelcase */

			const response = await fetch( this.instanceVars.ajax_url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: new URLSearchParams( requestBody )
			} );

			const data = await response.json();

			if ( data.success ) {

				// Update the results container with the new HTML
				this.resultsContainer.innerHTML = data.data.html;

				// Update the pagination controls with the new pagination HTML
				this.paginationControls.innerHTML = data.data.paginationHtml;

				// Update the total number of brokers
				this.updateBrokerCount( data.data.totalBrokers );

				// Update the current page from the response data
				this.currentPage = parseInt( data.data.currentPage || 1, 10 );

				// Update the URL parameters
				this.updateUrlParams();

				// Update the pagination state after currentPage is updated
				this.updatePaginationState();

				// Update pagination state
				this.totalPages = data.data.totalPages;
				this.currentPage = data.data.currentPage;

				// Update next button state
				const nextButton = this.paginationControls.querySelector( '.brokers-pagination__next-page' );
				if ( nextButton ) {

					if ( this.currentPage >= this.totalPages ) {

						nextButton.classList.add( 'inactive' );

					} else {

						nextButton.classList.remove( 'inactive' );

					}

				}

			} else {

				// eslint-disable-next-line no-console
				console.error( 'Error fetching data brokers:', data.message );
				this.resultsContainer.innerHTML = '<p>An error occurred while fetching data. Please try again later.</p>';


				// Update the current page from the response data
				this.currentPage = parseInt( data.data.currentPage || 1, 10 );

				// Update the URL parameters
				// this.updateUrlParams();

			}

		} catch ( error ) {

			// eslint-disable-next-line no-console
			console.error( 'Error updating results:', error );
			this.resultsContainer.innerHTML = '<p>An error occurred while fetching data. Please try again later.</p>';


		}

	}

	/**
	 * Initializes the event listeners for the filters inputs.
	 */
	initFiltersInputs() {

		this.filtersInputs = this.dataBrokersNavigationBlock.querySelectorAll( '.brokers-filters__form input' );

		this.filtersInputs.forEach( ( input ) => {

			// Remove existing event listeners
			input.removeEventListener( 'change', this.handleFilterChange );

			// Add new event listener
			input.addEventListener( 'change', this.handleFilterChange );

		} );

	}

	/**
	 * Handles the change event for the filters inputs.
	 * @param {Event} event - The change event.
	 */
	handleFilterChange = ( event ) => {

		const input = event.target;

		const cleanKey = input.name.replace( '[]', '' );
		this.updateFilters( cleanKey, input.value, input.dataset.slug, input.checked );

	};


	/**
	 * Updates the displayed total number of data brokers.
	 * @param {number} totalBrokers - The total number of data brokers.
	 */
	updateBrokerCount( totalBrokers ) {

		// Update desktop view (separate count and text elements)
		const countElement = this.dataBrokersNavigationBlock.querySelector( '.amount-of-brokers__count' );
		const textElement = this.dataBrokersNavigationBlock.querySelector( '.amount-of-brokers__text' );
		if ( countElement && textElement ) {

			countElement.textContent = totalBrokers;
			textElement.textContent = totalBrokers === 1 ? 'DATA BROKER' : 'DATA BROKERS';

		}

		// Update mobile view (show results button)
		const mobileResultsCount = this.dataBrokersNavigationBlock.querySelector( '.js-show-results span' );
		if ( mobileResultsCount ) {

			mobileResultsCount.textContent = totalBrokers;

		}

	}

	/**
	 * Closes the filters panel and hides the overlay.
	 */
	closeFilters() {

		// Start sliding down filters
		this.filtersContainer.classList.remove( 'active' );
		document.body.classList.remove( 'filters-open' );

		// Wait for animation to complete before hiding overlay
		setTimeout( () => {

			this.filtersOverlay.classList.remove( 'active' );

		}, 300 ); // Match this with the CSS transition duration

	}

	/**
	 * Opens the filters panel and shows the overlay.
	 */
	openFilters() {

		// Show overlay first
		this.filtersOverlay.classList.add( 'active' );
		document.body.classList.add( 'filters-open' );

		// Slight delay to ensure overlay is visible before sliding up filters
		requestAnimationFrame( () => {

			this.filtersContainer.classList.add( 'active' );

		} );

	}

	/**
	 * Creates a debounced function that delays invoking func until after wait milliseconds have elapsed since the last time the debounced function was invoked.
	 * @param {Function} func - The function to debounce.
	 * @param {number} delay - The number of milliseconds to delay.
	 * @return {Function} The debounced function.
	 */
	debounce( func, delay ) {

		let timeoutId;
		// eslint-disable-next-line func-names
		return function ( ...args ) {

			clearTimeout( timeoutId );
			timeoutId = setTimeout( () => func.apply( this, args ), delay );

		};

	}

	/**
	 * Updates the view switcher state.
	 */
	updateViewSwitcherState() {

		this.viewSwitcherButtons.forEach( ( button ) => {

			if ( button.dataset.type === this.currentViewType ) {

				button.classList.add( 'active' );

			} else {

				button.classList.remove( 'active' );

			}

		} );

	}

	/**
	 * Updates the pagination state.
	 */
	updatePaginationState() {

		const prevButton = this.paginationControls.querySelector( '.brokers-pagination__prev-page' );
		const nextButton = this.paginationControls.querySelector( '.brokers-pagination__next-page' );


		if ( prevButton ) {

			prevButton.classList.toggle( 'inactive', this.currentPage === 1 );

		}

		if ( nextButton ) {

			nextButton.classList.toggle( 'inactive', this.currentPage === this.totalPages );

		}

	}


	/**
	 *
	 * Function to update the filters object and trigger an update to the results
	 *
	 * @param {*} key
	 * @param {*} inputValue
	 * @param {*} slug
	 * @param {*} checked
	 */
	updateFilters( key, inputValue, slug, checked = true ) {

		if ( !this.currentFilters[key] ) {

			this.currentFilters[key] = [];

		}

		const filterItem = { value: inputValue, slug: slug };

		if ( checked ) {

			if ( !this.currentFilters[key].some( item => item.slug === slug ) ) {

				this.currentFilters[key].push( filterItem );

			}

		} else {

			this.currentFilters[key] = this.currentFilters[key].filter( item => item.slug !== slug );

		}

		this.currentPage = 1; // Reset to first page when filters change
		this.updateUrlParams();
		this.updateResults(); // Only trigger AJAX update here

	}

	/**
	 * Updates the URL parameters using a more SEO-friendly format
	 */
	updateUrlParams() {

		const urlParts = [];
		const filterGroups = {};

		// Include broker_page parameter
		if ( this.currentPage > 1 || this.currentPage === this.initialPage ) {

			urlParts.push( `broker_page=${this.currentPage}` );

		}

		// Add view type
		if ( this.currentViewType === 'grid' ) {

			urlParts.push( `view=${this.currentViewType}` );

		}

		// Handle search parameter
		if ( this.searchInput.value ) {

			urlParts.push( `search=${encodeURIComponent( this.searchInput.value )}` );

		}

		// Group filter values by key
		Object.entries( this.currentFilters ).forEach( ( [key, values] ) => {

			if ( key !== 'search' && values.length > 0 ) {

				// Special handling for expanded-reach to ensure it's properly included
				filterGroups[key] = values.map( item => item.slug );

			}

		} );

		// Add grouped filters to URL
		Object.entries( filterGroups ).forEach( ( [key, slugs] ) => {

			if ( slugs.length > 0 ) {

				urlParts.push( `${key}=${encodeURIComponent( slugs.join( ',' ) )}` );

			}

		} );

		const newUrl = `${window.location.pathname}${urlParts.length ? '?' : ''}${urlParts.join( '&' )}`;
		window.history.pushState( {}, '', newUrl );

	}

	/**
	 * Initializes filters from URL parameters
	 */
	initializeFiltersFromUrl() {

		const searchParams = new URLSearchParams( window.location.search );

		// Reset current filters
		this.currentFilters = {};

		// Handle search parameter
		if ( searchParams.has( 'search' ) && this.searchInput ) {

			this.searchInput.value = searchParams.get( 'search' );

		}

		// Process filter parameters
		for ( const [key, value] of searchParams.entries() ) {

			// Skip non-filter parameters
			if ( ['broker_page', 'view', 'search'].includes( key ) ) {

				continue;

			}

			// Split comma-separated values
			const values = value.split( ',' ).map( v => v.trim() ).filter( Boolean );

			this.currentFilters[key] = [];

			values.forEach( slug => {

				const input = this.dataBrokersNavigationBlock.querySelector(
					`input[name="${key}[]"][data-slug="${slug}"]`
				);

				if ( input ) {

					input.checked = true;
					this.currentFilters[key].push( {
						value: input.value,
						slug: slug
					} );

				}

			} );

		}

	}

	/**
	 * Clears all filters and resets the view
	 */
	clearAllFilters() {

		// Reset all checkboxes
		this.filtersInputs.forEach( input => {

			input.checked = false;

		} );

		// Clear search input
		if ( this.searchInput ) {

			this.searchInput.value = '';

		}

		// Reset filters object
		this.currentFilters = {};

		// Reset to first page
		this.currentPage = 1;

		// Clear URL parameters and update browser history
		window.history.pushState( {}, '', window.location.pathname );

		// Update results
		this.updateResults();

	}

	/**
	 * Handles window resize events and switches view type if necessary
	 */
	handleResize() {

		if ( window.innerWidth <= 1024 && this.currentViewType === 'list' ) {

			this.currentViewType = 'grid';
			this.updateViewSwitcherState();
			this.updateUrlParams();
			this.updateResults();

		}

	}

	/**
	 * Displays search suggestions in the suggestions container
	 * @param {Array} suggestions - Array of suggestion objects
	 * @return {void}
	 */
	displaySuggestions( suggestions ) {

		if ( !suggestions || !suggestions.length ) {

			const html = `
				<ul class="brokers-search__suggestions-list">
					<li class="brokers-search__suggestion-item brokers-search__suggestion-item--no-results">
						<span class="suggestion-name">No matches found</span>
					</li>
				</ul>
			`;

			this.suggestionsContainer.innerHTML = html;
			this.showSuggestions();
			return;

		}

		const html = `
			<ul class="brokers-search__suggestions-list">
				${suggestions.map( item => `
					<li class="brokers-search__suggestion-item" data-id="${item.id}">
						<span class="suggestion-name">${item.name}</span>
					</li>
				` ).join( '' )}
			</ul>
		`;

		this.suggestionsContainer.innerHTML = html;
		this.showSuggestions();

		// Add click events to suggestions (only for actual results)
		const suggestionItems = this.suggestionsContainer.querySelectorAll( '.brokers-search__suggestion-item:not(.brokers-search__suggestion-item--no-results)' );
		suggestionItems.forEach( item => {

			item.addEventListener( 'click', this.handleSuggestionClick );

		} );

	}

	/**
	 * Handles the search input event and fetches suggestions
	 * @param {Event} event - The input event object
	 * @return {Promise<void>}
	 */
	async handleSearchInput( event ) {

		const searchTerm = event.target.value.trim();

		if ( searchTerm.length < 2 ) {

			this.hideSuggestions();
			return;

		}

		try {

			const requestBody = {
				action: 'get_data_broker_suggestions',
				nonce: this.instanceVars.security,
				search: searchTerm,
				filters: JSON.stringify( this.currentFilters ) // Stringify filters like in updateResults
			};

			const response = await fetch( this.instanceVars.ajax_url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: new URLSearchParams( requestBody )
			} );

			const data = await response.json();

			if ( data.success ) {

				const suggestions = data.data.suggestions.map( name => ( {
					name: name
				} ) );
				this.displaySuggestions( suggestions );

			} else {

				this.displaySuggestions( [] ); // Show no results message

			}

		} catch ( error ) {

			// eslint-disable-next-line no-console
			console.error( 'Error fetching suggestions:', error );
			this.displaySuggestions( [] ); // Show no results message on error

		}

	}

	/**
	 * Handles click event on a suggestion item
	 * @param {Event} event - The click event object
	 * @return {void}
	 */
	handleSuggestionClick( event ) {

		const item = event.currentTarget;
		const name = item.querySelector( '.suggestion-name' ).textContent;

		this.searchInput.value = name;
		this.hideSuggestions();
		this.updateFilters( 'search', name );

	}

	/**
	 * Shows the suggestions container
	 * @return {void}
	 */
	showSuggestions() {

		this.suggestionsContainer.style.display = 'block';
		this.isSuggestionsVisible = true;

	}

	/**
	 * Hides the suggestions container
	 * @return {void}
	 */
	hideSuggestions() {

		this.suggestionsContainer.style.display = 'none';
		this.isSuggestionsVisible = false;

	}

	/**
	 * Handles clicks outside the suggestions container
	 * @param {Event} event - The click event object
	 * @return {void}
	 */
	handleClickOutside( event ) {

		if ( this.isSuggestionsVisible &&
			!this.suggestionsContainer.contains( event.target ) &&
			!this.searchInput.contains( event.target ) ) {

			this.hideSuggestions();

		}

	}

	/**
	 * Sets up event listeners for the suggestions functionality
	 * @return {void}
	 */
	setupSuggestionsEventListeners() {

		this.searchInput.addEventListener( 'input', ( event ) => {

			if ( window.innerWidth <= 1024 ) {

				this.handleSearchInput( event );

			}

		} );

		document.addEventListener( 'click', this.handleClickOutside );

	}

}
